import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { Bell, MessageCircle, Music, PlayCircle, Radio, X, DollarSign } from "lucide-react";
import { platformClient } from "@/lib/musicApi";
import { getSocket } from "@/lib/socket";
import { usePlatformAuth } from "@/contexts/PlatformAuthContext";

const ACCENT = "var(--accent-primary)";
const TOKEN_KEY = "samtwo_platform_token";

const ICONS = { comment_reply: MessageCircle, dm_reply: MessageCircle, new_track: Music, new_video: PlayCircle, live_now: Radio, live_scheduled: Radio, tip: DollarSign };

const timeAgo = (iso, t) => {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return t("notifications.time.justNow");
  if (s < 3600) return t("notifications.time.minutes", { n: Math.floor(s / 60) });
  if (s < 86400) return t("notifications.time.hours", { n: Math.floor(s / 3600) });
  return t("notifications.time.days", { n: Math.floor(s / 86400) });
};

// Notification title/body render in the subscriber's language via the stored i18n
// key + params; older rows without a key fall back to the English text on the row.
const notifText = (t, key, params, fallback) => (key ? t(key, params || {}) : fallback);

const NotifIcon = ({ type }) => {
  const Icon = ICONS[type] || Bell;
  return <Icon size={16} style={{ color: ACCENT }} className="flex-shrink-0 mt-0.5" />;
};

export const NotificationBell = () => {
  const { isSubscriber } = usePlatformAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [banner, setBanner] = useState(null);
  const bannerTimer = useRef(null);
  const panelRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await platformClient.get("/notifications", { params: { limit: 20 } });
      setItems(data.items);
      setUnread(data.unread_count);
    } catch {}
  }, []);

  // Initial load + socket subscription for real-time pushes.
  useEffect(() => {
    if (!isSubscriber) return;
    load();
    const socket = getSocket();
    const token = localStorage.getItem(TOKEN_KEY);
    const identify = () => { if (token) socket.emit("identify", { token }); };
    identify();
    socket.on("connect", identify);
    const onNew = (n) => {
      setItems((prev) => [n, ...prev.filter((x) => x.id !== n.id)]);
      setUnread((u) => u + 1);
      setBanner(n);
      if (bannerTimer.current) clearTimeout(bannerTimer.current);
      bannerTimer.current = setTimeout(() => setBanner(null), 4000);
    };
    socket.on("new-notification", onNew);
    return () => {
      socket.off("new-notification", onNew);
      socket.off("connect", identify);
      if (bannerTimer.current) clearTimeout(bannerTimer.current);
    };
  }, [isSubscriber, load]);

  // Close panel on outside click.
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  if (!isSubscriber) return null;

  const openItem = async (n) => {
    setOpen(false);
    setBanner(null);
    if (!n.is_read) {
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, is_read: true } : x)));
      setUnread((u) => Math.max(0, u - 1));
      platformClient.patch(`/notifications/${n.id}/read`).catch(() => {});
    }
    if (n.target_url) navigate(n.target_url);
  };

  const markAllRead = async () => {
    setItems((prev) => prev.map((x) => ({ ...x, is_read: true })));
    setUnread(0);
    platformClient.patch("/notifications/read").catch(() => {});
  };

  const deleteItem = (e, n) => {
    e.stopPropagation();
    setItems((prev) => prev.filter((x) => x.id !== n.id));
    if (!n.is_read) setUnread((u) => Math.max(0, u - 1));
    platformClient.delete(`/notifications/${n.id}`).catch(() => {});
  };

  const badge = unread > 9 ? "9+" : unread;

  return (
    <>
      {/* Slide-in toast banner (4s) */}
      <AnimatePresence>
        {banner && (
          <motion.button
            initial={{ y: -80, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -80, opacity: 0 }}
            transition={{ type: "spring", stiffness: 320, damping: 28 }}
            onClick={() => openItem(banner)}
            data-testid="notification-toast"
            className="fixed top-4 left-1/2 -translate-x-1/2 z-[95] flex items-start gap-2.5 px-4 py-3 rounded-2xl text-left max-w-sm w-[92%]"
            style={{ background: "var(--bg-surface)", border: `1px solid ${ACCENT}`, boxShadow: "0 12px 40px rgba(0,0,0,0.18)" }}
          >
            <NotifIcon type={banner.type} />
            <div className="min-w-0">
              <p className="text-sm font-semibold truncate" style={{ color: "var(--text-primary)" }}>{notifText(t, banner.title_key, banner.params, banner.title)}</p>
              {(banner.body_key || banner.body) && <p className="text-xs truncate" style={{ color: "var(--text-secondary)" }}>{notifText(t, banner.body_key, banner.params, banner.body)}</p>}
            </div>
          </motion.button>
        )}
      </AnimatePresence>

      <div className="hide-in-video-fs fixed top-4 right-4 z-[80]" ref={panelRef}>
        <button
          onClick={() => { setOpen((v) => !v); if (!open) load(); }}
          data-testid="notification-bell"
          className="relative w-11 h-11 rounded-full flex items-center justify-center"
          style={{ background: "var(--bg-surface)", border: "1px solid var(--border-color)", boxShadow: "0 2px 10px rgba(0,0,0,0.08)" }}
          aria-label={t("notifications.bellAria")}
        >
          <Bell size={20} style={{ color: "var(--text-primary)" }} />
          {unread > 0 && (
            <span data-testid="notification-badge" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center" style={{ background: "#ef4444", color: "#fff" }}>{badge}</span>
          )}
        </button>

        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, y: -8, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -8, scale: 0.98 }}
              transition={{ duration: 0.16 }}
              data-testid="notification-panel"
              className="absolute right-0 mt-2 w-[360px] max-w-[92vw] rounded-2xl overflow-hidden"
              style={{ background: "var(--bg-surface)", border: "1px solid var(--border-color)", boxShadow: "0 16px 48px rgba(0,0,0,0.2)" }}
            >
              <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: "var(--border-color)" }}>
                <span className="text-sm font-bold" style={{ color: "var(--text-primary)" }}>{t("notifications.panelTitle")}</span>
                {unread > 0 && <button onClick={markAllRead} data-testid="notification-mark-all-read" className="text-xs font-semibold" style={{ color: ACCENT }}>{t("notifications.markAllRead")}</button>}
              </div>
              <div className="max-h-[420px] overflow-y-auto">
                {items.length === 0 ? (
                  <p className="text-sm text-center py-10" style={{ color: "var(--text-secondary)" }} data-testid="notification-empty">{t("notifications.empty")}</p>
                ) : items.map((n) => (
                  <div
                    key={n.id}
                    data-testid={`notification-item-${n.id}`}
                    className="w-full flex items-start gap-2.5 px-4 py-3 border-b group"
                    style={{
                      borderColor: "var(--border-color)",
                      borderLeft: n.is_read ? "3px solid transparent" : `3px solid ${ACCENT}`,
                      background: n.is_read ? "transparent" : "color-mix(in srgb, var(--accent-primary) 6%, transparent)",
                    }}
                  >
                    <button onClick={() => openItem(n)} className="flex items-start gap-2.5 text-left min-w-0 flex-1">
                      <NotifIcon type={n.type} />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm" style={{ color: "var(--text-primary)", fontWeight: n.is_read ? 400 : 600 }}>{notifText(t, n.title_key, n.params, n.title)}</p>
                        {(n.body_key || n.body) && <p className="text-xs mt-0.5" style={{ color: "var(--text-secondary)" }}>{notifText(t, n.body_key, n.params, n.body)}</p>}
                        <p className="text-[11px] mt-1" style={{ color: "var(--text-secondary)" }}>{timeAgo(n.created_at, t)}</p>
                      </div>
                    </button>
                    <button onClick={(e) => deleteItem(e, n)} data-testid={`notification-delete-${n.id}`} aria-label={t("notifications.remove")}
                      className="flex-shrink-0 p-1 rounded-full opacity-50 hover:opacity-100" style={{ color: "var(--text-secondary)" }}>
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
};

export default NotificationBell;
